import {
  CircleDot,
  Cloud,
  Crosshair,
  Globe2,
  Grid3X3,
  Loader2,
  MapPin,
  Minus,
  Moon,
  Orbit,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Route,
  Scan,
  Satellite,
  X,
} from 'lucide-react';
import { lazy, Suspense, useRef, useState } from 'react';
import { ViewportCaptureControls } from '@/scene/ViewportCaptureControls';
import { focusViewportCamera, zoomViewportCamera, type CameraFocusMode } from '@/scene/CameraRig';
import type { GlobeCoordinates } from '@/scene/earthCoordinates';
import { useThrottledPlayhead } from '@/hooks/useThrottledPlayhead';
import { BODY_IDS, FLIGHT_PHASES, findIndexForTime } from '@/lib/telemetry-series';
import { pause, play, seek } from '@/lib/playbackClock';
import { useMissionStore } from '@/stores/missionStore';
import { useTelemetryStore } from '@/stores/telemetryStore';

const GlobeScene = lazy(() => import('@/scene/GlobeScene'));

function Loading() {
  return (
    <div className="flex h-full items-center justify-center gap-2 text-xs text-faint">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading 3D viewport…
    </div>
  );
}

interface SceneLayers {
  clouds: boolean;
  graticule: boolean;
  groundTrack: boolean;
  targetOrbit: boolean;
  trajectory: boolean;
  celestialContext: boolean;
  sphereOfInfluence: boolean;
}

function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const secs = total % 60;
  const clock = [hours, minutes, secs].map((value) => value.toString().padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${clock}` : clock;
}

const formatCoordinate = (value: number, positive: string, negative: string): string =>
  `${Math.abs(value).toFixed(2)}° ${value >= 0 ? positive : negative}`;

/** Keeps three.js/@react-three out of the initial bundle — a user who never opens this panel never downloads it. */
export function Viewport3DPanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusMode, setFocusMode] = useState<CameraFocusMode>('globe');
  const [hoveredCoordinate, setHoveredCoordinate] = useState<GlobeCoordinates | null>(null);
  const [inspectionPoint, setInspectionPoint] = useState<GlobeCoordinates | null>(null);
  const [layers, setLayers] = useState<SceneLayers>({
    clouds: true,
    graticule: true,
    groundTrack: true,
    targetOrbit: true,
    trajectory: true,
    celestialContext: true,
    sphereOfInfluence: false,
  });
  const status = useTelemetryStore((state) => state.status);
  const summary = useTelemetryStore((state) => state.summary);
  const configuredDestination = useMissionStore((state) => state.document?.destinationBody);
  const { time, duration, playing, speed } = useThrottledPlayhead(10);
  const series = status === 'ready' ? useTelemetryStore.getState().series : null;
  const index = series ? findIndexForTime(series.t, time) : -1;
  const body = index >= 0 && series ? BODY_IDS[series.centralBodyCode[index]!] : undefined;
  const phase = index >= 0 && series ? FLIGHT_PHASES[series.phaseCode[index]!] : undefined;
  const altitude = index >= 0 && series ? series.altitude[index]! : undefined;
  const vehicleSpeed = index >= 0 && series ? series.speed[index]! : undefined;
  const latitude = index >= 0 && series ? series.latitudeDeg[index]! : undefined;
  const longitude = index >= 0 && series ? series.longitudeDeg[index]! : undefined;
  const lunarMission = summary?.destinationBodyId === 'moon' || configuredDestination === 'moon';
  const displayedCoordinate = hoveredCoordinate ?? inspectionPoint;
  const controls: {
    mode: CameraFocusMode;
    label: string;
    description: string;
    icon: typeof Globe2;
  }[] = [
    {
      mode: 'globe',
      label: 'Whole Earth',
      description: 'Frame the entire rotating Earth and all visible trajectory layers.',
      icon: Globe2,
    },
    {
      mode: 'mission',
      label: 'Fit mission',
      description: 'Frame the complete geocentric trajectory, including an Earth–Moon transfer.',
      icon: Scan,
    },
    {
      mode: 'launch',
      label: 'Launch site',
      description: 'Move the camera to the mission launch site for an ascent-scale view.',
      icon: MapPin,
    },
    {
      mode: 'vehicle',
      label: 'Follow vehicle',
      description: 'Track the simulated vehicle as the playback time changes.',
      icon: Crosshair,
    },
    ...(lunarMission
      ? [
          {
            mode: 'destination' as const,
            label: 'Moon',
            description: 'Follow the moving Moon and lunar arrival geometry.',
            icon: Moon,
          },
        ]
      : []),
  ];
  const layerControls: {
    key: keyof SceneLayers;
    label: string;
    description: string;
    icon: typeof Cloud;
  }[] = [
    {
      key: 'clouds',
      label: 'Cloud layer',
      description: 'Visual context only; this is not the weather profile used by the simulation.',
      icon: Cloud,
    },
    {
      key: 'graticule',
      label: 'Latitude/longitude grid',
      description: 'Reference lines fixed to the rotating Earth.',
      icon: Grid3X3,
    },
    {
      key: 'groundTrack',
      label: 'Ground-track layer',
      description: 'Projection of the Earth-centred trajectory onto the rotating surface.',
      icon: Route,
    },
    {
      key: 'targetOrbit',
      label: 'Target-orbit layer',
      description: 'The mission’s configured target orbit, distinct from the simulated path.',
      icon: Orbit,
    },
    {
      key: 'trajectory',
      label: 'Flight-path layer',
      description: 'The propagated three-dimensional vehicle path from the latest run.',
      icon: Satellite,
    },
    {
      key: 'celestialContext',
      label: 'Moon and reference orbit',
      description: 'Show the ephemeris-driven Moon and its geocentric reference orbit.',
      icon: Moon,
    },
    {
      key: 'sphereOfInfluence',
      label: 'Sphere of influence',
      description: 'Show the lunar sphere-of-influence boundary used for the frame hand-off.',
      icon: CircleDot,
    },
  ];

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-[#01050d]">
      <Suspense fallback={<Loading />}>
        <GlobeScene
          showClouds={layers.clouds}
          showGraticule={layers.graticule}
          showGroundTrack={layers.groundTrack}
          showTargetOrbit={layers.targetOrbit}
          showTrajectory={layers.trajectory}
          showCelestialContext={layers.celestialContext}
          showSphereOfInfluence={layers.sphereOfInfluence}
          inspectionPoint={inspectionPoint}
          onCoordinateHover={setHoveredCoordinate}
          onCoordinateSelect={setInspectionPoint}
        />
      </Suspense>
      <div
        className="absolute left-3 top-3 flex max-w-[calc(100%-6rem)] gap-1 rounded-panel border border-border/80 bg-surface-raised/90 p-1 shadow-lg backdrop-blur-md"
        aria-label="3D camera views"
      >
        {controls.map(({ mode, label, description, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            onClick={() => {
              setFocusMode(mode);
              focusViewportCamera(mode);
            }}
            title={`${label} — ${description}`}
            aria-label={label}
            aria-pressed={focusMode === mode}
            className={`flex h-7 items-center gap-1 rounded-control px-2 text-[11px] transition-colors ${
              focusMode === mode
                ? 'bg-accent text-accent-contrast shadow-sm'
                : 'text-muted hover:bg-surface-sunken hover:text-text'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden min-[560px]:inline">{label}</span>
          </button>
        ))}
      </div>
      <div
        className="absolute right-2 top-11 flex flex-col gap-1 rounded-panel border border-border/80 bg-surface-raised/90 p-1 shadow-lg backdrop-blur-md"
        aria-label="3D map layers"
      >
        {layerControls.map(({ key, label, description, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setLayers((current) => ({ ...current, [key]: !current[key] }))}
            title={`${layers[key] ? 'Hide' : 'Show'} ${label.toLowerCase()} — ${description}`}
            aria-label={label}
            aria-pressed={layers[key]}
            className={`flex h-7 w-7 items-center justify-center rounded-control transition-colors ${
              layers[key]
                ? 'bg-info/15 text-info'
                : 'text-faint hover:bg-surface-sunken hover:text-text'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
        <div className="mx-1 h-px bg-border" aria-hidden />
        <button
          type="button"
          onClick={() => zoomViewportCamera('in')}
          title="Zoom in — move the camera 28% closer to its current focus"
          aria-label="Zoom 3D Earth in"
          className="flex h-7 w-7 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-sunken hover:text-text"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => zoomViewportCamera('out')}
          title="Zoom out — move the camera farther from its current focus"
          aria-label="Zoom 3D Earth out"
          className="flex h-7 w-7 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-sunken hover:text-text"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
      </div>
      {status === 'running' && (
        <div className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-surface-raised/90 px-3 py-1.5 text-xs text-muted shadow-lg backdrop-blur-md">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-info" />
          Propagating mission…
        </div>
      )}
      {status === 'ready' && (
        <div className="absolute left-3 top-12 flex items-center gap-1.5 rounded-panel border border-border/80 bg-surface-raised/90 p-1 text-[10px] shadow-lg backdrop-blur-md">
          <button
            type="button"
            onClick={() => (playing ? pause() : play())}
            aria-label={playing ? 'Pause 3D mission playback' : 'Play 3D mission playback'}
            title={playing ? 'Pause 3D mission playback' : 'Play 3D mission playback'}
            className="flex h-7 w-7 items-center justify-center rounded-control bg-accent text-accent-contrast"
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => {
              pause();
              seek(0);
            }}
            aria-label="Return 3D playback to launch"
            title="Return 3D playback to launch"
            className="flex h-7 w-7 items-center justify-center rounded-control border border-border text-muted hover:bg-surface-sunken hover:text-text"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-28 tabular-nums text-text">
            {formatElapsed(time)} / {formatElapsed(duration)}
          </span>
          <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-faint">{speed}×</span>
        </div>
      )}
      {status === 'ready' && index >= 0 && series && (
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-panel border border-border/80 bg-surface-raised/90 px-3 py-2 text-xs shadow-lg backdrop-blur-md">
          <div className="mb-1 flex items-center gap-2">
            <strong className="capitalize text-text">{phase?.replaceAll('-', ' ')}</strong>
            <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 capitalize text-faint">
              {body}-centred
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 tabular-nums text-muted">
            <span>Altitude/radius</span>
            <span className="text-right text-text">{(altitude! / 1000).toFixed(0)} km</span>
            <span>Speed</span>
            <span className="text-right text-text">{(vehicleSpeed! / 1000).toFixed(2)} km/s</span>
            {body === 'earth' && (
              <>
                <span>Position</span>
                <span className="text-right text-text">
                  {formatCoordinate(latitude!, 'N', 'S')} · {formatCoordinate(longitude!, 'E', 'W')}
                </span>
              </>
            )}
          </div>
          {body !== 'earth' && (
            <p className="mt-1 max-w-64 border-t border-border pt-1 text-info">
              Moon-centred telemetry is transformed into the continuous Earth–Moon display frame.
            </p>
          )}
        </div>
      )}
      <div className="absolute bottom-3 right-3 max-w-56 rounded-panel border border-border/80 bg-surface-raised/90 px-3 py-2 text-xs shadow-lg backdrop-blur-md">
        {displayedCoordinate ? (
          <>
            <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-wide text-faint">
              <span>{hoveredCoordinate ? 'Surface cursor' : 'Pinned location'}</span>
              {inspectionPoint && !hoveredCoordinate && (
                <button
                  type="button"
                  onClick={() => setInspectionPoint(null)}
                  aria-label="Clear pinned globe location"
                  title="Clear pinned location"
                  className="flex h-5 w-5 items-center justify-center rounded-control text-faint hover:bg-surface-sunken hover:text-text"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="mt-1 flex gap-3 font-medium tabular-nums text-text">
              <span>{formatCoordinate(displayedCoordinate.latitudeDeg, 'N', 'S')}</span>
              <span>{formatCoordinate(displayedCoordinate.longitudeDeg, 'E', 'W')}</span>
            </div>
            {hoveredCoordinate && (
              <p className="mt-1 text-[10px] text-faint">Click the globe to pin this point.</p>
            )}
          </>
        ) : (
          <p className="text-[10px] leading-4 text-faint">
            Drag to orbit · scroll to zoom · right-drag to pan · click Earth to inspect coordinates
          </p>
        )}
      </div>
      <ViewportCaptureControls containerRef={containerRef} />
    </div>
  );
}
